/**
 * Transaction Validator
 * Utilities for validating and detecting issues in bank statement transactions
 */

/**
 * Month name to number mapping
 */
const MONTH_MAP = {
  'jan': '01', 'january': '01',
  'feb': '02', 'february': '02',
  'mar': '03', 'march': '03',
  'apr': '04', 'april': '04',
  'may': '05',
  'jun': '06', 'june': '06',
  'jul': '07', 'july': '07',
  'aug': '08', 'august': '08',
  'sep': '09', 'sept': '09', 'september': '09',
  'oct': '10', 'october': '10',
  'nov': '11', 'november': '11',
  'dec': '12', 'december': '12'
};

/**
 * Normalize partial dates to full dates using statement year
 * Converts "03 Aug" → "03/08/2018" (if statementYear is 2018)
 * @param {string} dateStr - Date string (may be partial like "03 Aug")
 * @param {string|number} statementYear - Year from user input
 * @returns {string} Normalized date in DD/MM/YYYY format or original if already valid
 */
export const normalizePartialDate = (dateStr, statementYear) => {
  if (!dateStr || typeof dateStr !== 'string') {
    return dateStr;
  }

  const trimmed = dateStr.trim();

  // If already in valid format (DD/MM/YYYY, DD/MM/YY, YYYY-MM-DD), return as is
  if (
    /^\d{2}\/\d{2}\/\d{4}$/.test(trimmed) ||  // DD/MM/YYYY
    /^\d{2}\/\d{2}\/\d{2}$/.test(trimmed) ||   // DD/MM/YY
    /^\d{4}-\d{2}-\d{2}$/.test(trimmed)        // YYYY-MM-DD
  ) {
    return trimmed;
  }

  // Handle 2-part slash dates: MM/DD or DD/MM (e.g., "11/16" from API)
  if (/^\d{1,2}\/\d{1,2}$/.test(trimmed) && statementYear) {
    const slashParts = trimmed.split('/');
    const part1 = parseInt(slashParts[0]);
    const part2 = parseInt(slashParts[1]);
    let day, month;

    if (part1 > 12 && part2 > 12) {
      // Both parts > 12, can't be valid - return as is
      return trimmed;
    } else if (part1 > 12) {
      // Must be DD/MM (e.g., "25/11")
      day = part1;
      month = part2;
    } else if (part2 > 12) {
      // Must be MM/DD (e.g., "11/16")
      month = part1;
      day = part2;
    } else {
      // Ambiguous - default to MM/DD (API uses US format)
      month = part1;
      day = part2;
    }

    const paddedDay = day.toString().padStart(2, '0');
    const paddedMonth = month.toString().padStart(2, '0');
    return `${paddedDay}/${paddedMonth}/${statementYear}`;
  }

  // Try to parse partial date format: "DD MMM" or "DD Month"
  // Examples: "03 Aug", "20 August", "15 Jan"
  const partialDateMatch = trimmed.match(/^(\d{1,2})\s+([a-zA-Z]+)$/);

  if (partialDateMatch && statementYear) {
    const day = partialDateMatch[1].padStart(2, '0'); // Ensure 2 digits
    const monthName = partialDateMatch[2].toLowerCase();
    const monthNumber = MONTH_MAP[monthName];

    if (monthNumber) {
      // Convert to DD/MM/YYYY format
      return `${day}/${monthNumber}/${statementYear}`;
    }
  }

  // Return original if we can't parse it
  return trimmed;
};

/**
 * Validate a date string
 * Accepts formats: DD/MM/YYYY, DD/MM/YY, YYYY-MM-DD
 * @param {string} dateStr - Date string to validate
 * @returns {Object} Validation result
 */
export const validateDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') {
    return {
      isValid: false,
      error: 'Date is required'
    };
  }

  const trimmedDate = dateStr.trim();

  // Check for explicit "Unknown Date"
  if (trimmedDate === 'Unknown Date' || trimmedDate === '') {
    return {
      isValid: false,
      error: 'Unknown date',
      isUnknown: true
    };
  }

  // Check for incomplete date marker from backend
  if (trimmedDate.startsWith('INCOMPLETE:')) {
    return {
      isValid: false,
      error: 'Date is missing day component',
      isIncomplete: true,
      partialDate: trimmedDate.replace('INCOMPLETE:', '')
    };
  }

  // Check for month-only patterns (Feb, Feb 2024, February, etc.)
  const monthOnlyPattern = /^[a-zA-Z]+(\s+\d{4})?$/;
  if (monthOnlyPattern.test(trimmedDate)) {
    return {
      isValid: false,
      error: 'Date is missing day component',
      isIncomplete: true,
      partialDate: trimmedDate
    };
  }

  // Check for 2-part slash dates (MM/DD or DD/MM without year)
  if (/^\d{1,2}\/\d{1,2}$/.test(trimmedDate)) {
    return {
      isValid: false,
      isIncomplete: true,
      error: 'Date is missing year component'
    };
  }

  // Test common date formats
  const formats = [
    /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
    /^\d{2}\/\d{2}\/\d{2}$/,  // DD/MM/YY
    /^\d{4}-\d{2}-\d{2}$/     // YYYY-MM-DD
  ];

  const matchesFormat = formats.some(format => format.test(trimmedDate));

  if (!matchesFormat) {
    return {
      isValid: false,
      error: 'Invalid date format (use DD/MM/YYYY or DD/MM/YY)'
    };
  }

  // Try to parse and validate the date is realistic
  let day, month, year;

  if (trimmedDate.includes('/')) {
    const parts = trimmedDate.split('/');
    day = parseInt(parts[0]);
    month = parseInt(parts[1]);
    year = parseInt(parts[2]);

    // Convert 2-digit year to 4-digit
    if (year < 100) {
      year = year < 50 ? 2000 + year : 1900 + year;
    }
  } else if (trimmedDate.includes('-')) {
    const parts = trimmedDate.split('-');
    year = parseInt(parts[0]);
    month = parseInt(parts[1]);
    day = parseInt(parts[2]);
  }

  // Validate ranges
  if (day < 1 || day > 31) {
    return {
      isValid: false,
      error: 'Day must be between 1 and 31'
    };
  }

  if (month < 1 || month > 12) {
    return {
      isValid: false,
      error: 'Month must be between 1 and 12'
    };
  }

  if (year < 1990 || year > 2050) {
    return {
      isValid: false,
      error: 'Year must be between 1990 and 2050'
    };
  }

  return {
    isValid: true,
    day,
    month,
    year
  };
};

/**
 * Validate a description
 * @param {string} description - Description to validate
 * @returns {Object} Validation result
 */
export const validateDescription = (description) => {
  if (!description || typeof description !== 'string') {
    return {
      isValid: false,
      error: 'Description is required',
      isUnknown: true
    };
  }

  const trimmedDesc = description.trim();

  if (trimmedDesc === '' || trimmedDesc === 'Unknown Transaction') {
    return {
      isValid: false,
      error: 'Unknown description',
      isUnknown: true
    };
  }

  if (trimmedDesc.length < 2) {
    return {
      isValid: false,
      error: 'Description too short (minimum 2 characters)'
    };
  }

  if (trimmedDesc.length > 200) {
    return {
      isValid: false,
      error: 'Description too long (maximum 200 characters)'
    };
  }

  return {
    isValid: true
  };
};

/**
 * Validate an amount
 * @param {string|number} amount - Amount to validate
 * @returns {Object} Validation result
 */
export const validateAmount = (amount) => {
  if (amount === null || amount === undefined || amount === '') {
    return {
      isValid: false,
      error: 'Amount is required',
      isUnknown: true
    };
  }

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    return {
      isValid: false,
      error: 'Amount must be a valid number'
    };
  }

  // Note: Zero amounts are valid (e.g., balance inquiries, fee waivers, reversals)
  // Removed the check that flagged zero as invalid

  // Reasonable amount range check
  if (Math.abs(numAmount) > 10000000) {
    return {
      isValid: false,
      error: 'Amount exceeds reasonable range'
    };
  }

  return {
    isValid: true,
    value: numAmount
  };
};

/**
 * Detect all issues in a transaction
 * @param {Object} transaction - Transaction to check
 * @returns {Object} Issue detection result
 */
export const detectIssues = (transaction) => {
  const dateValidation = validateDate(transaction.date);
  const descValidation = validateDescription(transaction.description);
  const amountValidation = validateAmount(transaction.amount);

  const issues = {
    hasUnknownDate: !dateValidation.isValid && dateValidation.isUnknown,
    hasInvalidDate: !dateValidation.isValid && !dateValidation.isUnknown && !dateValidation.isIncomplete,
    hasIncompleteDate: !dateValidation.isValid && dateValidation.isIncomplete,
    dateError: dateValidation.error,

    hasUnknownDescription: !descValidation.isValid && descValidation.isUnknown,
    hasInvalidDescription: !descValidation.isValid && !descValidation.isUnknown,
    descriptionError: descValidation.error,

    hasUnknownAmount: !amountValidation.isValid && amountValidation.isUnknown,
    hasInvalidAmount: !amountValidation.isValid && !amountValidation.isUnknown,
    amountError: amountValidation.error
  };

  // Calculate overall severity
  const hasUnknown = issues.hasUnknownDate || issues.hasUnknownDescription || issues.hasUnknownAmount;
  const hasInvalid = issues.hasInvalidDate || issues.hasInvalidDescription || issues.hasInvalidAmount;
  const hasIncomplete = issues.hasIncompleteDate;

  issues.severity = hasUnknown ? 'warning' : (hasInvalid || hasIncomplete ? 'error' : 'ok');
  issues.hasAnyIssue = hasUnknown || hasInvalid || hasIncomplete;

  return issues;
};

/**
 * Validate an entire transaction object
 * @param {Object} transaction - Transaction to validate
 * @returns {Object} Complete validation result
 */
export const validateTransaction = (transaction) => {
  if (!transaction || typeof transaction !== 'object') {
    return {
      isValid: false,
      error: 'Transaction must be an object'
    };
  }

  const dateValidation = validateDate(transaction.date);
  const descValidation = validateDescription(transaction.description);
  const amountValidation = validateAmount(transaction.amount);

  const isValid = dateValidation.isValid && descValidation.isValid && amountValidation.isValid;

  return {
    isValid,
    dateValidation,
    descValidation,
    amountValidation,
    issues: detectIssues(transaction)
  };
};

/**
 * Calculate quality statistics for a list of transactions
 * @param {Array} transactions - Array of transactions
 * @returns {Object} Quality statistics
 */
export const calculateQualityStats = (transactions) => {
  if (!Array.isArray(transactions) || transactions.length === 0) {
    return {
      total: 0,
      unknownDates: 0,
      unknownDescriptions: 0,
      unknownAmounts: 0,
      invalidDates: 0,
      invalidDescriptions: 0,
      invalidAmounts: 0,
      incompleteDates: 0,
      qualityScore: 100,
      hasIssues: false
    };
  }

  let unknownDates = 0;
  let unknownDescriptions = 0;
  let unknownAmounts = 0;
  let invalidDates = 0;
  let invalidDescriptions = 0;
  let invalidAmounts = 0;
  let incompleteDates = 0;

  transactions.forEach(transaction => {
    const issues = detectIssues(transaction);

    if (issues.hasUnknownDate) unknownDates++;
    if (issues.hasUnknownDescription) unknownDescriptions++;
    if (issues.hasUnknownAmount) unknownAmounts++;
    if (issues.hasInvalidDate) invalidDates++;
    if (issues.hasInvalidDescription) invalidDescriptions++;
    if (issues.hasInvalidAmount) invalidAmounts++;
    if (issues.hasIncompleteDate) incompleteDates++;
  });

  const totalIssues = unknownDates + unknownDescriptions + unknownAmounts +
                      invalidDates + invalidDescriptions + invalidAmounts + incompleteDates;

  const qualityScore = transactions.length > 0
    ? Math.round(((transactions.length * 3 - totalIssues) / (transactions.length * 3)) * 100)
    : 100;

  return {
    total: transactions.length,
    unknownDates,
    unknownDescriptions,
    unknownAmounts,
    invalidDates,
    invalidDescriptions,
    invalidAmounts,
    incompleteDates,
    qualityScore: Math.max(0, qualityScore),
    hasIssues: totalIssues > 0
  };
};

/**
 * Format a transaction for CSV export
 * Ensures all fields are properly escaped and formatted
 * @param {Object} transaction - Transaction to format
 * @returns {string} CSV-formatted row
 */
export const formatTransactionForCSV = (transaction) => {
  const date = transaction.date || 'Unknown Date';
  let description = (transaction.description || 'Unknown Transaction').trim();
  const amount = parseFloat(transaction.amount || 0).toFixed(2);

  // Escape description if it contains comma
  if (description.includes(',') || description.includes('"')) {
    description = `"${description.replace(/"/g, '""')}"`;
  }

  return `${date},${description},${amount}`;
};

/**
 * Generate CSV content from transactions
 * @param {Array} transactions - Array of transactions
 * @returns {string} Complete CSV content
 */
/**
 * Split transactions containing "Foreign Amount: X.XX" into two entries:
 * the original transaction (with cleaned description) and a new Bank Charge transaction.
 * @param {Array} transactions - Array of transactions
 * @returns {Array} Transactions with foreign amounts split into separate Bank Charge entries
 */
export const splitForeignAmountTransactions = (transactions) => {
  const foreignAmountPattern = /^(.+?)\s+Foreign Amount:\s*([\d,.]+)\s*$/i;

  return transactions.flatMap(trans => {
    const match = trans.description?.match(foreignAmountPattern);
    if (!match) return [trans];

    const cleanDescription = match[1].trim();
    const chargeAmount = parseFloat(match[2].replace(',', ''));
    if (isNaN(chargeAmount) || chargeAmount === 0) return [trans];

    return [
      { ...trans, description: cleanDescription },
      { ...trans, description: 'Bank Charge', amount: `-${chargeAmount.toFixed(2)}` }
    ];
  });
};

export const generateCSVContent = (transactions) => {
  if (!Array.isArray(transactions) || transactions.length === 0) {
    return 'Date,Description,Amount\n';
  }

  const headers = 'Date,Description,Amount';
  const rows = transactions.map(formatTransactionForCSV);

  return [headers, ...rows].join('\n');
};
